import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Save, X } from 'lucide-react';
import { api, parseApiError } from '../lib/api';
import { Card } from '../components/Card';

// T10 — owner-managed notification delegation. Delegates absorb the org's
// routine pings per category; the owner only hears must-reach events when a
// category has no delegate (or opts back into copies). Role requests and
// billing are never delegable, so they don't appear here.

type DiscordMember = {
  discordId: string;
  username: string;
  displayName: string;
  avatar: string | null;
};

const CATEGORIES: Array<{ key: 'onboarding' | 'competitions'; label: string; hint: string }> = [
  { key: 'onboarding', label: 'Onboarding', hint: 'clan applications, seat claims, claim links' },
  { key: 'competitions', label: 'Competitions', hint: 'disputes, score flags, OCR issues' },
];

// ── Per-category picker ───────────────────────────────────────────────────────
// Owns its own search + paste state. All hooks declared before any early return.

function CategoryPicker({
  label,
  hint,
  ids,
  nameCache,
  orgId,
  onAdd,
  onRemove,
}: {
  label: string;
  hint: string;
  ids: string[];
  nameCache: Record<string, string>;
  orgId: string;
  onAdd: (id: string, displayName?: string) => void;
  onRemove: (id: string) => void;
}) {
  // ALL hooks at the top — no early returns before this block
  const [discordQuery, setDiscordQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pasteId, setPasteId] = useState('');
  const [pasteError, setPasteError] = useState('');
  const [showPaste, setShowPaste] = useState(false);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedQuery(discordQuery.trim().replace(/^@+/, '')),
      300,
    );
    return () => clearTimeout(t);
  }, [discordQuery]);

  const memberSearch = useQuery({
    queryKey: ['orgDiscordMembers', orgId, debouncedQuery],
    queryFn: () => api.searchOrgDiscordMembers(orgId, debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  });

  function handlePickMember(m: DiscordMember) {
    onAdd(m.discordId, m.displayName);
    setDiscordQuery('');
    setDebouncedQuery('');
  }

  function handleAddPasteId() {
    const id = pasteId.trim();
    if (!/^\d{17,20}$/.test(id)) {
      setPasteError(`"${id}" doesn't look like a Discord user ID (17–20 digits).`);
      return;
    }
    onAdd(id);
    setPasteId('');
    setPasteError('');
  }

  const chipLabel = (id: string) => nameCache[id] ?? `…${id.slice(-6)}`;

  return (
    <div>
      <label className="mb-2 block font-display text-[10px] font-black uppercase tracking-wider text-iron-300">
        {label}{' '}
        <span className="font-normal normal-case text-iron-500">— {hint}</span>
      </label>

      {/* Current-delegate chips */}
      {ids.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {ids.map(id => (
            <span
              key={id}
              className="flex items-center gap-1 border border-iron-700 bg-iron-950 px-2 py-0.5 font-mono text-[11px] text-iron-200"
            >
              {chipLabel(id)}
              <button
                onClick={() => onRemove(id)}
                className="ml-0.5 text-iron-500 hover:text-iron-200"
                aria-label={`Remove ${chipLabel(id)}`}
              >
                <X className="h-3 w-3" strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Name-search picker */}
      <div className="relative">
        <input
          value={discordQuery}
          onChange={e => setDiscordQuery(e.target.value)}
          placeholder="Type a name to add a delegate…"
          className="input w-full text-sm"
        />

        {debouncedQuery.length >= 2 && (
          <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto border border-iron-700 bg-iron-950 shadow-forged">
            {memberSearch.isFetching ? (
              <p className="flex items-center gap-2 px-3 py-2 text-xs text-iron-400">
                <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2.5} />
                Searching…
              </p>
            ) : memberSearch.isError ? (
              <p className="px-3 py-2 text-xs text-red-300">
                Search unavailable — you can paste an ID below.
              </p>
            ) : (memberSearch.data?.members.length ?? 0) === 0 ? (
              <p className="px-3 py-2 text-xs text-iron-500">
                No matches — you can paste an ID below.
              </p>
            ) : (
              memberSearch.data!.members.map(m => (
                <button
                  key={m.discordId}
                  onClick={() => handlePickMember(m)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-iron-950"
                >
                  {m.avatar ? (
                    <img
                      src={`https://cdn.discordapp.com/avatars/${m.discordId}/${m.avatar}.png?size=32`}
                      alt=""
                      className="h-6 w-6 shrink-0 rounded-full"
                    />
                  ) : (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-iron-700 font-display text-[9px] font-black text-iron-200">
                      {m.displayName.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className="flex-1 truncate text-sm text-iron-100">
                    {m.displayName}
                  </span>
                  <span className="font-display text-[9px] font-black uppercase tracking-wider text-forge-300">
                    Add
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Paste-ID fallback (collapsible) */}
      <div className="mt-2">
        <button
          onClick={() => {
            setShowPaste(p => !p);
            setPasteError('');
            setPasteId('');
          }}
          className="font-display text-[9px] font-black uppercase tracking-wider text-iron-500 hover:text-iron-300"
        >
          {showPaste ? '▲ hide' : '▼ paste an ID'}
        </button>
        {showPaste && (
          <div className="mt-1.5 flex gap-2">
            <input
              value={pasteId}
              onChange={e => {
                setPasteId(e.target.value);
                setPasteError('');
              }}
              onKeyDown={e => e.key === 'Enter' && handleAddPasteId()}
              placeholder="17–20 digit Discord user ID"
              className="input flex-1 font-mono text-xs"
            />
            <button
              onClick={handleAddPasteId}
              className="btn-primary font-display text-[10px] font-black uppercase tracking-wider"
            >
              Add
            </button>
          </div>
        )}
        {pasteError && (
          <p className="mt-1 text-xs text-amber-400">{pasteError}</p>
        )}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function NotificationDelegatesPanel({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['orgNotifyConfig', orgId],
    queryFn: () => api.getOrgNotifyConfig(orgId),
  });

  const [ownerCopyAll, setOwnerCopyAll] = useState(false);
  const [delegateIds, setDelegateIds] = useState<
    Record<'onboarding' | 'competitions', string[]>
  >({ onboarding: [], competitions: [] });
  // Populated when the user picks via the name-search picker.
  // IDs loaded from the server (names unknown) fall back to short-id display.
  const [nameCache, setNameCache] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!data) return;
    setOwnerCopyAll(data.ownerCopyAll);
    setDelegateIds({
      onboarding: data.delegates.onboarding ?? [],
      competitions: data.delegates.competitions ?? [],
    });
  }, [data]);

  function handleAdd(
    key: 'onboarding' | 'competitions',
    id: string,
    displayName?: string,
  ) {
    setDelegateIds(prev => {
      if (prev[key].includes(id)) return prev; // dedupe — already present
      return { ...prev, [key]: [...prev[key], id] };
    });
    if (displayName) {
      setNameCache(prev => ({ ...prev, [id]: displayName }));
    }
  }

  function handleRemove(key: 'onboarding' | 'competitions', id: string) {
    setDelegateIds(prev => ({ ...prev, [key]: prev[key].filter(x => x !== id) }));
  }

  const mut = useMutation({
    mutationFn: () =>
      api.setOrgNotifyConfig(orgId, {
        ownerCopyAll,
        delegates: {
          onboarding: delegateIds.onboarding,
          competitions: delegateIds.competitions,
        },
      }),
    onSuccess: () => {
      setError('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ['orgNotifyConfig', orgId] });
    },
    onError: (e: Error) => {
      setSaved(false);
      setError(parseApiError(e).message);
    },
  });

  return (
    <Card>
      <p className="mb-4 text-xs text-iron-400">
        While a category has a delegate, you stop receiving those DMs — role requests
        and billing always come to you.
      </p>

      {isLoading ? (
        <p className="flex items-center gap-2 text-xs text-iron-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </p>
      ) : (
        <div className="space-y-5">
          {CATEGORIES.map(cat => (
            <CategoryPicker
              key={cat.key}
              label={cat.label}
              hint={cat.hint}
              ids={delegateIds[cat.key]}
              nameCache={nameCache}
              orgId={orgId}
              onAdd={(id, displayName) => handleAdd(cat.key, id, displayName)}
              onRemove={id => handleRemove(cat.key, id)}
            />
          ))}

          <label className="flex items-center gap-2 text-xs text-iron-300">
            <input
              type="checkbox"
              checked={ownerCopyAll}
              onChange={e => setOwnerCopyAll(e.target.checked)}
            />
            Also send me a copy of everything delegates receive
          </label>

          {error && <p className="text-xs text-red-400">{error}</p>}

          <button
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            className="btn-primary morph flex items-center gap-2 font-display text-[11px] font-black uppercase tracking-wider"
          >
            {mut.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            {saved ? 'Saved ✓' : 'Save routing'}
          </button>
        </div>
      )}
    </Card>
  );
}
